// src/utils/cropImage.js
//
// Draws a square crop region (as reported by react-easy-crop's
// onCropComplete) onto a canvas and exports it as a compressed JPEG File,
// iteratively lowering quality (and, if needed, resolution) until it fits
// under maxBytes. Output is always JPEG since PNG has no lossy quality knob
// to control file size with.

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1];
const MIN_OUTPUT_SIZE = 100;

export async function getCroppedFile(
  imageSrc,
  cropPixels,
  fileName,
  maxBytes = 100 * 1024,
  outputSize = 500
) {
  const image = await loadImage(imageSrc);

  let size = outputSize;
  let smallestBlob = null;

  while (size >= MIN_OUTPUT_SIZE) {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      size,
      size
    );

    for (const quality of QUALITY_STEPS) {
      const candidate = await canvasToBlob(canvas, quality);
      if (!candidate) continue;

      if (!smallestBlob || candidate.size < smallestBlob.size) {
        smallestBlob = candidate;
      }
      if (candidate.size <= maxBytes) {
        return new File([candidate], fileName, { type: "image/jpeg" });
      }
    }

    size = Math.floor(size / 2);
  }

  // Every size/quality combination stayed over budget (very small maxBytes,
  // or an unusually dense image) — fall back to the smallest blob produced
  // rather than failing the upload outright.
  console.warn(
    `Could not compress image under ${maxBytes} bytes; using smallest result (${smallestBlob?.size} bytes).`
  );
  return new File([smallestBlob], fileName, { type: "image/jpeg" });
}
